import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Alert, Badge, Button, Card, Form, Modal, Spinner } from 'react-bootstrap';
import axios from 'axios';

const errorMessage = error => typeof error.response?.data === 'string'
  ? error.response.data : error.response?.data?.message || error.message;

export default function StagingDetails() {
  const { siteId } = useParams();
  const endpoint = `/api/sites/${encodeURIComponent(siteId)}/staging/`;
  const [site, setSite] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [review, setReview] = useState(null);
  const [confirmed, setConfirmed] = useState(false);

  const load = async () => {
    try { setSite((await axios.get(endpoint)).data); setError(''); }
    catch (e) { setError(errorMessage(e)); }
  };
  useEffect(() => { load(); }, [siteId]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    setBusy(true); setError('');
    try { setSite((await axios.post(endpoint)).data); setMessage('Staging is ready. Live files are unchanged.'); }
    catch (e) { setError(errorMessage(e)); }
    finally { setBusy(false); }
  };
  const reviewAction = async kind => {
    setBusy(true); setError(''); setConfirmed(false);
    try {
      const result = (await axios.get(`${endpoint}review/`, { params: { action: kind } })).data;
      setReview({ ...result, operation_id: crypto.randomUUID().replaceAll('-', '') });
    } catch (e) { setError(errorMessage(e)); }
    finally { setBusy(false); }
  };
  const publish = async () => {
    setBusy(true); setError('');
    try {
      const result = await axios.post(`${endpoint}${review.kind}/`, {
        live_digest: review.live_digest, staging_digest: review.staging_digest,
        operation_id: review.operation_id, confirm_live_changes: confirmed,
      });
      setSite(result.data); setReview(null);
      setMessage(review.kind === 'rollback' ? 'Previous live content restored. Staging is unchanged.' : 'Staging published. The previous live content is available for rollback.');
    } catch (e) {
      setError(errorMessage(e));
      if (e.response?.status === 409) setReview(null);
    } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm('Remove staging and its rollback backup? This leaves the live website unchanged.')) return;
    setBusy(true); setError('');
    try { await axios.delete(endpoint); await load(); setMessage('Staging removed. Live files are unchanged.'); }
    catch (e) { setError(errorMessage(e)); }
    finally { setBusy(false); }
  };

  return <div className="pb-4">
    <Link to={`/sites/${siteId}`}>← Back to site</Link>
    <h1 className="mt-3">{site?.name || 'Site'}: staging</h1>
    <p className="text-muted">An independent copy for reviewing changes before publishing to the live website.</p>
    {error && <Alert variant="danger">{error}{error.includes('Sign in') && <> <Link to="/login?reauth=1">Sign in again</Link></>}</Alert>}
    {message && <Alert variant="success" dismissible onClose={() => setMessage('')}>{message}</Alert>}
    {!site && !error && <Spinner animation="border" />}
    {site && !site.exists && <Card><Card.Body>
      <Card.Title>No staging environment yet</Card.Title>
      <p>Create a separate copy of the current live files. Existing live editing and addresses keep working.</p>
      <Button onClick={create} disabled={busy}>{busy ? 'Creating staging…' : 'Create staging'}</Button>
    </Card.Body></Card>}
    {site?.exists && <>
      <Card className="mb-3"><Card.Body>
        <Card.Title>Staging <Badge bg="secondary">Separate from live</Badge></Card.Title>
        <p className="text-break"><a href={site.preview_url} target="_blank" rel="noreferrer">{site.preview_url}</a></p>
        <p>Preview pages ask search engines not to index them. Forms, embedded frames and external background requests are disabled in the preview. The URL is shareable, not password protected.</p>
        <p className="text-muted">Hidden/private files and archive files are excluded from staging and preserved on live during publication.</p>
        <div className="d-flex gap-2 flex-wrap">
          {site.preview_ready ? <Button as="a" href={site.preview_url} target="_blank" rel="noreferrer" variant="outline-primary">Open staging</Button>
            : <Button onClick={create} disabled={busy}>Activate preview</Button>}
          <Button as={Link} to={`/sites/${siteId}/staging/files/`}>Edit staging files</Button>
          <Button variant="success" onClick={() => reviewAction('publish')} disabled={busy}>Publish to live</Button>
        </div>
      </Card.Body></Card>
      {site.live_changed && <Alert variant="warning">Live has changed since this staging copy was created or last published. Review those changes before replacing live content.</Alert>}
      <Card className="mb-3"><Card.Body>
        <Card.Title>Previous live version</Card.Title>
        <p>{site.backup ? `Saved ${new Date(site.backup.created_at * 1000).toLocaleString()}. Rolling back leaves staging untouched.` : 'Publishing saves the current live content here for one-step rollback.'}</p>
        <Button variant="outline-secondary" disabled={busy || !site.backup} onClick={() => reviewAction('rollback')}>Roll back live</Button>
      </Card.Body></Card>
      <Button variant="outline-danger" onClick={remove} disabled={busy}>Remove staging</Button>
    </>}
    <Modal show={!!review} onHide={() => !busy && setReview(null)} size="lg" backdrop="static">
      <Modal.Header closeButton={!busy}><Modal.Title>{review?.kind === 'rollback' ? 'Review rollback' : 'Review publication'}</Modal.Title></Modal.Header>
      <Modal.Body>
        <p>This replaces live content on these addresses, including file deletions. The current live version will be saved for rollback.</p>
        <ul>{review?.domains.map(domain => <li className="text-break" key={domain}>{domain}</li>)}</ul>
        {['added', 'changed', 'deleted'].map(kind => <div key={kind} className="mb-3">
          <strong className="text-capitalize">{kind}: {review?.changes[kind].length || 0}</strong>
          {!!review?.changes[kind].length && <ul style={{ maxHeight: 150, overflowY: 'auto' }}>
            {review.changes[kind].map(path => <li className="text-break" key={path}>{path}</li>)}
          </ul>}
        </div>)}
        {review?.live_changed && <Alert variant="warning">
          <p>Live has changed since staging was copied or last published. Publishing this replacement may overwrite those edits.</p>
          <Form.Check id="confirm-live-changes" label="I have reviewed the live changes and want to replace them." checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />
        </Alert>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" disabled={busy} onClick={() => setReview(null)}>Cancel</Button>
        <Button variant="success" disabled={busy || (review?.live_changed && !confirmed)} onClick={publish}>
          {busy ? 'Applying…' : review?.kind === 'rollback' ? 'Restore previous live version' : 'Publish reviewed staging'}
        </Button>
      </Modal.Footer>
    </Modal>
  </div>;
}
